import { useEffect, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '@clerk/react'
import { LIVE_CREATORS, type Creator, type CreatorService } from '../constants/creators'
import { CreatorAvatar } from '../components/creators/CreatorAvatar'
import { useForcedTheme } from '../hooks/useForcedTheme'

/**
 * A bookingUrl is bookable only if it's actually an http(s) URL. Catches
 * accidental sentinel strings (e.g. "TBD") that survived the type system
 * because `bookingUrl?: string` permits any string when present.
 */
function isBookable(bookingUrl: string | undefined): bookingUrl is string {
  return typeof bookingUrl === 'string' && /^https?:\/\//.test(bookingUrl)
}

// Alternating accents (gold / violet) across creators for visual rhythm.
const ACCENTS = ['gold', 'violet'] as const
type Accent = (typeof ACCENTS)[number]

const accentMap = {
  gold: {
    border: 'border-gold/30',
    borderHover: 'hover:border-gold/50',
    bg: 'bg-gold/6',
    text: 'text-gold-light',
    dot: 'bg-gold',
    // theme-audit: unmapped rgba(212,168,70,0.12) — legacy amber glow, no palette token
    glow: 'hover:shadow-[0_0_30px_rgba(212,168,70,0.12)]',
  },
  violet: {
    border: 'border-astral-violet/30',
    borderHover: 'hover:border-astral-violet/50',
    bg: 'bg-astral-violet/6',
    text: 'text-astral-violet-light',
    dot: 'bg-astral-violet',
    glow: 'hover:shadow-[0_0_30px_color-mix(in_oklab,var(--color-glow-purple)_15%,transparent)]',
  },
} as const

function ServiceCard({
  service,
  accent,
  bookingUrl,
}: {
  service: CreatorService
  accent: Accent
  bookingUrl?: string
}) {
  const a = accentMap[accent]
  // Per-service URL wins over creator-level bookingUrl when present, so
  // a creator with distinct destinations per offering (e.g. Adrienne) can
  // link each card to its own landing page.
  const effectiveUrl = service.url ?? bookingUrl
  const bookable = isBookable(effectiveUrl)
  // CTA copy adapts to the offering shape — "Book" doesn't fit ordering a
  // deck or applying to a program. The price slot carries the verb when
  // it's not a dollar amount.
  const isMoneyPrice = /^\$/.test(service.price)
  const ctaText = isMoneyPrice
    ? 'Book This Session'
    : service.price === 'Free'
      ? 'Start Free'
      : service.price === 'Apply'
        ? 'Apply Now'
        : service.price === 'Order'
          ? 'Order Deck'
          : 'Visit'

  return (
    <div
      className={`
        group relative bg-bg-surface/60 backdrop-blur-xs rounded-2xl
        border ${a.border} ${a.borderHover} ${a.glow}
        transition-all duration-500 overflow-hidden
      `}
    >
      <div className={`absolute top-0 inset-x-0 h-px ${a.bg}`} />
      <div className="p-6 md:p-8">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <h3 className={`font-display text-xl md:text-2xl tracking-wide ${a.text}`}>
              {service.title}
            </h3>
          </div>
          <div className="text-right shrink-0">
            <span className={`font-display text-2xl ${a.text}`}>{service.price}</span>
            {service.duration && (
              <p className="font-body text-xs text-text-muted mt-0.5">{service.duration}</p>
            )}
          </div>
        </div>

        {service.description && (
          <p className="font-body text-sm text-text-primary/80 leading-relaxed mb-6">
            {service.description}
          </p>
        )}

        {bookable ? (
          <a
            href={effectiveUrl}
            target="_blank"
            rel="noopener noreferrer"
            // aria-label includes service title so screen readers distinguish
            // between identical visible CTAs on the page — WCAG 2.4.4.
            aria-label={`${ctaText}: ${service.title}`}
            className={`
              flex items-center justify-center gap-2 w-full sm:w-auto sm:inline-flex px-6 py-3.5 sm:py-3
              bg-surface-raised/80 border ${a.border} ${a.borderHover}
              ${a.text} font-display tracking-cosmic-wide text-xs uppercase
              rounded-full transition-all duration-300
              hover:bg-hairline active:scale-[0.97]
            `}
          >
            {ctaText}
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" className="opacity-60">
              <path d="M5 3l4 4-4 4" />
            </svg>
          </a>
        ) : (
          <span className="inline-block px-6 py-3 text-xs uppercase tracking-cosmic-wide font-display text-text-muted/60 border border-hairline rounded-full">
            Booking link coming soon
          </span>
        )}
      </div>
    </div>
  )
}

function CreatorBlock({ creator, accent }: { creator: Creator; accent: Accent }) {
  const a = accentMap[accent]
  const bookable = isBookable(creator.bookingUrl)

  return (
    <section className="px-4 md:px-8 pb-12 max-w-5xl mx-auto">
      {/* Creator header */}
      <div className="relative bg-bg-surface/40 backdrop-blur-xs rounded-2xl border border-hairline p-6 md:p-8 mb-8 overflow-hidden">
        <div className="absolute top-0 inset-x-0 h-px bg-linear-to-r from-transparent via-gold/20 to-transparent" />
        <div className="flex flex-col md:flex-row gap-6 items-start">
          <CreatorAvatar
            creator={creator}
            size={112}
            className="w-24 h-24 md:w-28 md:h-28 rounded-full object-cover border border-gold/30 bg-cosmic-black/40 shrink-0 text-lg"
          />
          <div className="flex-1">
            <h2 className={`font-display text-2xl md:text-3xl tracking-wide ${a.text} mb-1`}>
              {creator.fullName ?? creator.name}
            </h2>
            <p className="font-body text-xs uppercase tracking-cosmic text-text-muted/80 mb-3">
              {creator.role}
            </p>
            <p className="font-body text-sm text-text-primary/80 leading-relaxed">
              {creator.bio}
            </p>
          </div>
        </div>
      </div>

      {/* Service grid */}
      <div className="grid gap-6 md:grid-cols-2">
        {creator.allServices.map((s) => (
          <ServiceCard
            // (title, price) is unique within a creator's catalog; composite
            // key survives titles repeating across creators or in future
            // SimplyBook catalogs.
            key={`${creator.id}|${s.title}|${s.price}`}
            service={s}
            accent={accent}
            bookingUrl={creator.bookingUrl}
          />
        ))}
      </div>

      {/* Book CTA */}
      <div className="mt-8 text-center">
        {bookable ? (
          <a
            href={creator.bookingUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block px-10 py-4 bg-deep-purple/60 border border-gold/30
                       text-gold-light font-display tracking-[0.2em] text-sm
                       rounded-full hover:bg-astral-violet/40 hover:border-gold/50
                       transition-all duration-500 active:scale-[0.97]"
          >
            Book with {creator.name} →
          </a>
        ) : (
          <span className="inline-block px-10 py-4 border border-hairline text-text-muted/70 font-display tracking-[0.2em] text-sm rounded-full">
            Booking link coming soon
          </span>
        )}
      </div>
    </section>
  )
}

/**
 * Tab bar for switching between practitioners. Pill-style buttons mirror the
 * design-system tokens used elsewhere. Stays horizontal-scrollable on narrow
 * screens (per `feedback_text_wrapping`/`feedback_bottomnav_labels` — solve
 * truncation via scroll, not by collapsing labels).
 */
function PractitionerTabs({
  creators,
  activeId,
  onSelect,
}: {
  creators: Creator[]
  activeId: string
  onSelect: (id: string) => void
}) {
  if (creators.length < 2) return null
  return (
    <div className="px-4 md:px-8 max-w-5xl mx-auto mb-8" role="tablist" aria-label="Choose a creator">
      <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1 snap-x">
        {creators.map((c) => {
          const isActive = c.id === activeId
          return (
            <button
              key={c.id}
              role="tab"
              type="button"
              aria-selected={isActive}
              onClick={() => onSelect(c.id)}
              className={`shrink-0 snap-start px-5 py-2.5 rounded-full font-display tracking-cosmic-wide text-xs uppercase transition-all duration-300 ${
                isActive
                  ? 'border border-gold/40 bg-gold/10 text-gold-light shadow-[0_0_20px_rgba(212,168,70,0.12)]'
                  : 'border border-hairline text-text-muted/80 hover:border-white/20 hover:text-text-primary/85'
              }`}
            >
              {c.name}
            </button>
          )
        })}
      </div>
    </div>
  )
}

export function ServicesPage() {
  const location = useLocation()
  const navigate = useNavigate()
  const { isSignedIn } = useAuth()

  // Dual-use public route: signed-out visitors see it as a marketing surface
  // (force Daylight); signed-in users keep their chosen theme. `undefined`
  // (Clerk still loading) is treated as signed-out=false so we don't force
  // before auth resolves — no flash of wrong forcing.
  useForcedTheme('light', { when: isSignedIn === false })

  // Selected creator id is mirrored to the URL hash so shareable links like
  // `/services#adrienne` open straight to that practitioner. Default to the
  // first creator when no/unknown hash is present.
  const hashSlug = location.hash.replace(/^#/, '')
  const resolvedSlug = LIVE_CREATORS.find((c) => c.id === hashSlug)?.id ?? LIVE_CREATORS[0].id
  const [activeId, setActiveId] = useState<string>(resolvedSlug)

  // Keep state in sync if the URL hash changes externally (e.g. back/forward
  // nav between two practitioner hashes).
  useEffect(() => {
    if (hashSlug && LIVE_CREATORS.some((c) => c.id === hashSlug) && hashSlug !== activeId) {
      setActiveId(hashSlug)
    }
  }, [hashSlug, activeId])

  useEffect(() => { window.scrollTo(0, 0) }, [])

  const handleSelect = (id: string) => {
    setActiveId(id)
    // Replace history entry instead of pushing — switching tabs shouldn't
    // pile up back-button states.
    navigate(`${location.pathname}#${id}`, { replace: true })
  }

  const activeCreator = LIVE_CREATORS.find((c) => c.id === activeId) ?? LIVE_CREATORS[0]
  const activeIndex = LIVE_CREATORS.findIndex((c) => c.id === activeCreator.id)
  const activeAccent = ACCENTS[activeIndex % ACCENTS.length]

  return (
    <div className="relative">
      {/* Ambient background */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute inset-0 bg-gradient-mystic" />
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-astral-violet/4 rounded-full blur-[120px]" />
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-gold/3 rounded-full blur-[100px]" />
      </div>

      <div className="relative z-10">
        {/* Hero */}
        <header className="px-6 pt-6 pb-8 md:pt-12 md:pb-16 text-center max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 mb-6 rounded-full border border-gold/20 bg-gold/4 fade-in">
            <div className="w-1.5 h-1.5 rounded-full bg-gold animate-pulse" />
            <span className="font-body text-xs tracking-cosmic text-gold-light/70 uppercase">
              Now booking sessions
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl md:text-6xl tracking-[0.06em] text-gold-light leading-tight fade-in">
            Personal sessions
          </h1>

          <div className="mt-4 h-px w-32 mx-auto bg-linear-to-r from-transparent via-gold/40 to-transparent fade-in-delay-1" />

          <p className="mt-6 font-body text-base sm:text-lg text-text-secondary max-w-xl mx-auto leading-relaxed fade-in-delay-1">
            Some of our creators also offer sessions. Choose a creator to see
            their offerings — each is booked through their own scheduling page.
          </p>
        </header>

        <PractitionerTabs
          creators={LIVE_CREATORS}
          activeId={activeCreator.id}
          onSelect={handleSelect}
        />

        {/* Selected creator's section */}
        <CreatorBlock
          key={activeCreator.id}
          creator={activeCreator}
          accent={activeAccent}
        />

        {/* Sessions callout */}
        <div className="px-4 md:px-8 max-w-5xl mx-auto mt-2 mb-8 flex items-center justify-center gap-3 text-text-muted/80 font-body text-xs tracking-wider fade-in-delay-3">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.2" className="opacity-50">
            <rect x="2" y="3" width="12" height="9" rx="1.5" />
            <path d="M5 14h6" />
            <path d="M8 12v2" />
          </svg>
          <span className="uppercase text-center">Sessions held over secure video &middot; Meet from anywhere</span>
        </div>

        {/* Final CTA */}
        <section className="px-6 pb-8 md:pb-12 text-center max-w-2xl mx-auto">
          <div className="h-px w-24 mx-auto bg-linear-to-r from-transparent via-gold/30 to-transparent mb-10" />

          <h2 className="font-display text-2xl md:text-3xl tracking-wide text-gold-light mb-4">
            Ready to Go Deeper?
          </h2>
          <p className="font-body text-sm text-text-secondary/70 max-w-md mx-auto leading-relaxed mb-8">
            Something already called you here. Choose a creator above, or
            {isSignedIn
              ? ' pull a card and see what surfaces.'
              : ' start with a free oracle pull and see what surfaces.'}
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to={isSignedIn ? '/find-clarity' : '/reading'}
              className="px-8 py-4 border border-hairline bg-hairline-faint
                         text-text-secondary font-display tracking-cosmic-wide text-xs uppercase
                         rounded-full hover:bg-hairline hover:text-gold-light/70
                         transition-all duration-300"
            >
              {isSignedIn ? 'Pull a Card' : 'Try a Free Pull'}
            </Link>
          </div>
        </section>

        {/* Footer */}
        <footer className="px-6 py-8 border-t border-hairline-faint text-center">
          <p className="font-body text-xs text-text-muted/70 tracking-wider">
            All sessions are virtual &middot; Secure video &middot; Worldwide
          </p>
        </footer>
      </div>
    </div>
  )
}
